import React, { useState } from "react";
import { useQuery } from "../../hooks/useQuery";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";

export default function Users() {
  const usersQ = useQuery(() => api.get("/users").then((r) => r.data));
  const rolesQ = useQuery(() => api.get("/roles").then((r) => r.data));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "warehouse_agent" });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "warehouse_agent" });
      usersQ.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      usersQ.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="users-page">
      <div className="flex justify-between items-end gap-3">
        <div>
          <h1 className="font-serif-bv text-3xl text-[#1B3022]">Users</h1>
          <p className="text-sm text-[#1B3022]/60">Operators with system access.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-user-btn" className="btn-gold rounded-sm px-4 py-2 text-xs inline-flex items-center gap-2">
          <Plus size={12} /> New user
        </button>
      </div>

      {open && (
        <form onSubmit={create} data-testid="new-user-form" className="mt-6 bg-white border border-[#1B3022]/15 rounded-sm p-5 grid sm:grid-cols-2 gap-3 max-w-2xl">
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 border border-[#1B3022]/30 bg-white rounded-sm text-sm" />
          <input required minLength={6} type="password" placeholder="Password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="px-3 py-2 border border-[#1B3022]/30 bg-white rounded-sm text-sm" />
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border border-[#1B3022]/30 bg-white rounded-sm text-sm" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="px-3 py-2 border border-[#1B3022]/30 bg-white rounded-sm text-sm">
            {(rolesQ.data?.roles || []).map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
          </select>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-xs text-[#1B3022]/60">Cancel</button>
            <button type="submit" className="btn-gold rounded-sm px-5 py-2 text-xs">Create</button>
          </div>
        </form>
      )}

      <div className="mt-6 bg-white border border-[#1B3022]/15 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-[#0F1D14] text-[#D4AF37]">
            <tr>
              {["Email", "Name", "Role", "Created", ""].map((h) => <th key={h} className="px-3 py-3 text-left text-[10px] tracking-widest uppercase font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {usersQ.loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#1B3022]/60"><Loader2 className="inline animate-spin" /></td></tr>}
            {(usersQ.data || []).map((u) => (
              <tr key={u.id} className="border-t border-[#1B3022]/10 hover:bg-[#F5EFE0]/40">
                <td className="px-3 py-2 font-mono text-xs text-[#1B3022]">{u.email}</td>
                <td className="px-3 py-2">{u.name || "—"}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] tracking-widest uppercase bg-[#D4AF37]/15 text-[#A88420] px-2 py-0.5 rounded-sm">{u.role}</span>
                </td>
                <td className="px-3 py-2 text-[#1B3022]/60 text-xs">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <button onClick={() => remove(u)} className="p-1.5 border border-[#C8102E]/50 rounded-sm hover:bg-[#C8102E]/10">
                    <Trash2 size={12} className="text-[#C8102E]" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
